// Tyre compounds and wear. Grip is a multiplier on the car's base tyre μ
// (tuned for fresh Medium-ish qualifying grip). Lap-time sensitivity at Sepang
// is about 0.55 s per 1 % of grip (scripts/qss-lap.ts), which is how the pace
// deltas below were turned into grip factors. Calibration: docs/research/race-model.md.

export type Compound = 'soft' | 'medium' | 'hard' | 'inter' | 'wet';

export interface CompoundSpec {
  id: Compound;
  name: string;
  letter: string;
  /** Pirelli designation at Sepang 2026 (C2/C3/C4) or tyre type. */
  pirelli: string;
  color: string;
  slick: boolean;
  /** Fresh grip on a dry track relative to the car's base μ. */
  dryGrip: number;
  /** Laps (full-distance race) until the performance cliff; wear 1 = cliff. */
  cliffLaps: number;
  /** Grip lost by the cliff (linear degradation over the stint). */
  degGrip: number;
}

// Sepang 2026 (docs/research/race-model.md §8): Soft −0.5 s, Hard +0.4 s vs Medium;
// deg ~0.25 / 0.22 / 0.13 s per lap per lap; cliff ~16 / 24 / 34 laps.
// Grip from lap time: 1 % grip ≈ 0.55 s/lap here (scripts/qss-lap.ts).
export const COMPOUNDS: Record<Compound, CompoundSpec> = {
  soft: { id: 'soft', name: 'Soft', letter: 'S', pirelli: 'C4', color: '#ff3b30', slick: true, dryGrip: 1.0, cliffLaps: 16, degGrip: 0.073 },
  medium: { id: 'medium', name: 'Medium', letter: 'M', pirelli: 'C3', color: '#ffd60a', slick: true, dryGrip: 0.991, cliffLaps: 24, degGrip: 0.096 },
  hard: { id: 'hard', name: 'Hard', letter: 'H', pirelli: 'C2', color: '#f2f2f2', slick: true, dryGrip: 0.9837, cliffLaps: 34, degGrip: 0.08 },
  inter: { id: 'inter', name: 'Intermediate', letter: 'I', pirelli: 'Inter', color: '#34c759', slick: false, dryGrip: 1, cliffLaps: 30, degGrip: 0.027 },
  wet: { id: 'wet', name: 'Wet', letter: 'W', pirelli: 'Wet', color: '#0a84ff', slick: false, dryGrip: 1, cliffLaps: 30, degGrip: 0.027 },
};

/** Grip lost per lap of running past the cliff (~0.6 s/lap per lap). */
const POST_CLIFF_GRIP_PER_LAP = 0.011;

export const DRY_COMPOUNDS: Compound[] = ['soft', 'medium', 'hard'];
export const ALL_COMPOUNDS: Compound[] = ['soft', 'medium', 'hard', 'inter', 'wet'];

/**
 * Lap-time penalty (% over a dry Medium lap) against track wetness 0..1, from
 * Sepang 2026 (race-model.md §5.3): slick ↔ inter crossover at ~0.13 (lap 8),
 * inter best around 0.3 (+8.3 %), inter ↔ wet at ~0.55 (estimate).
 */
const WET_PENALTY: Record<'slick' | 'inter' | 'wet', Array<[number, number]>> = {
  slick: [[0, 0], [0.05, 4], [0.1, 8], [0.13, 10.4], [0.2, 16], [0.3, 24], [0.4, 32], [0.5, 40], [0.6, 50], [0.8, 70], [1, 100]],
  inter: [[0, 12], [0.05, 11], [0.1, 10], [0.13, 10], [0.2, 9], [0.3, 8.3], [0.4, 8.5], [0.5, 10], [0.55, 11], [0.6, 12.5], [0.8, 18], [1, 30]],
  wet: [[0, 20], [0.05, 18], [0.1, 16], [0.13, 15.5], [0.2, 14], [0.3, 13], [0.4, 12.5], [0.5, 12], [0.55, 12], [0.6, 12.5], [0.8, 14], [1, 18]],
};

/** Lap-time penalty % to grip: 1 % grip ≈ 0.58 % lap time, compounding for big losses. */
function penaltyToGrip(pct: number): number {
  return Math.pow(1 + pct / 100, -1 / 0.58);
}

/**
 * Grip multiplier of a tyre type on a track with the given wetness (0 dry .. 1
 * standing water), ignoring wear, relative to a fresh Medium-ish slick in the dry.
 */
export function wetnessGrip(c: CompoundSpec, wetness: number): number {
  const w = Math.min(1, Math.max(0, wetness));
  const table = WET_PENALTY[c.slick ? 'slick' : c.id === 'inter' ? 'inter' : 'wet'];
  return penaltyToGrip(interpolate(table, w)) * (c.slick ? c.dryGrip : 0.991);
}

/** Track wetness where a tyre type is quickest (for the engineer). */
export function bestTyreFor(wetness: number): 'slick' | 'inter' | 'wet' {
  if (wetness < 0.13) return 'slick';
  return wetness < 0.55 ? 'inter' : 'wet';
}

function interpolate(curve: Array<[number, number]>, x: number): number {
  for (let i = 1; i < curve.length; i++) {
    const [x1, y1] = curve[i]!;
    if (x <= x1) {
      const [x0, y0] = curve[i - 1]!;
      return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
    }
  }
  return curve[curve.length - 1]![1];
}

/** Grip factor from wear (1 = at the cliff): linear fade, then falling away each lap past it. */
export function wearGrip(c: CompoundSpec, wear: number): number {
  const w = Math.max(0, wear);
  const linear = c.degGrip * Math.min(w, 1);
  const past = Math.max(0, w - 1) * c.cliffLaps * POST_CLIFF_GRIP_PER_LAP;
  return Math.max(0.6, 1 - linear - past);
}

export class TyreSet {
  wear = 0;
  laps = 0;
  /** 0 = cold off the blankets after a stop, 1 = in the window. */
  warmth: number;

  constructor(
    readonly compound: Compound,
    warm = true,
  ) {
    this.warmth = warm ? 1 : 0.4;
  }

  get spec(): CompoundSpec {
    return COMPOUNDS[this.compound];
  }

  /** Grip multiplier for the current wear, warmth and track wetness. */
  grip(wetness: number): number {
    const warm = 0.94 + 0.06 * this.warmth;
    return wetnessGrip(this.spec, wetness) * wearGrip(this.spec, this.wear) * warm;
  }

  /**
   * Advance wear and temperature. `work` is the tyre work this step relative to
   * a typical racing lap (1 per lap at reference pace); `scale` is the race-length
   * wear multiplier. Wet running wears slicks less and inters/wets more on a drying track.
   */
  step(work: number, scale: number, wetness: number, speed: number, dt: number): void {
    const spec = this.spec;
    let rate = 1 / spec.cliffLaps;
    // Treaded tyres overheat and chew themselves up on a dry track (×3 when bone dry);
    // slicks run cooler and wear less in the wet.
    if (!spec.slick) rate *= 1 + 2 * Math.max(0, 1 - wetness / 0.15);
    else rate *= 1 - 0.5 * Math.min(1, wetness * 2);
    this.wear += work * rate * scale;
    // Warm up within ~1 km of running, faster at speed.
    this.warmth = Math.min(1, this.warmth + (dt * Math.max(10, speed)) / 1200);
  }

}
